<template>
  <b-field
    :label="label"
    :type="hasError() ? 'is-danger' : ''"
    :message="errorMessage()"
    class="rich-text-field"
    :class="{ 'required': required }"
  >
    <template
      v-if="hasInfo"
      #label
    >
      {{ label }}
      <span>
        <b-tooltip
          type="is-light"
          :size="infoSize"
          :position="infoPosition"
          multilined
        >
          <i :class="infoIcon" />
          <template #content>
            <p v-if="infoType == 'text'">{{ info }}</p>
            <slot
              v-if="infoType == 'html'"
              name="info"
            >
              <div v-html="info" />
            </slot>
          </template>
        </b-tooltip>
      </span>
    </template>
    <div class="editor">
      <editor-menu-bar
        v-slot="{ commands, isActive }"
        :editor="editor"
      >
        <div class="menubar">
          <button
            type="button"
            class="button is-white is-small"
            :class="{ 'is-active': isActive.bold() }"
            @click="commands.bold"
          >
            <b-icon icon="format-bold" />
          </button>

          <button
            type="button"
            class="button is-white is-small"
            :class="{ 'is-active': isActive.underline() }"
            @click="commands.underline"
          >
            <b-icon icon="format-underline" />
          </button>

          <button
            type="button"
            class="button is-white is-small"
            :class="{ 'is-active': isActive.bullet_list() }"
            @click="commands.bullet_list"
          >
            <b-icon icon="format-list-bulleted" />
          </button>

          <button
            type="button"
            class="button is-white is-small"
            :class="{ 'is-active': isActive.ordered_list() }"
            @click="commands.ordered_list"
          >
            <b-icon icon="format-list-numbered" />
          </button>
        </div>
      </editor-menu-bar>

      <editor-content
        class="content"
        :editor="editor"
      />
    </div>
  </b-field>
</template>

<script>
import { Editor, EditorContent, EditorMenuBar } from "tiptap";
import {
  Blockquote,
  CodeBlock,
  HardBreak,
  Heading,
  HorizontalRule,
  OrderedList,
  BulletList,
  ListItem,
  TodoItem,
  TodoList,
  Link,
  Bold,
  Code,
  Italic,
  Strike,
  Underline,
  History,
} from "tiptap-extensions";
import InputBase from "./InputBase";
import { DialogProgrammatic as Dialog } from "buefy";

export default {
  components: {
    EditorContent,
    EditorMenuBar,
  },
  extends: InputBase,
  props: {
    value: {
      type: String,
      default: "",
    },
    hasInfo: {
      default: false,
      type: Boolean,
    },
    infoIcon: {
      default: "mdi mdi-help-circle-outline",
      type: String,
    },
    info: {
      default: "",
      type: String,
    },
    infoPosition: {
      default: "is-top",
      type: String,
    },
    infoSize: {
      default: "is-medium",
      type: String,
    },
    infoType: {
      default: "text",
      type: String,
    },
    required: {
      default: false,
      type: Boolean
    }
  },
  data() {
    return {
      editor: new Editor({
        content: this.value,
        extensions: [
          new Blockquote(),
          new BulletList(),
          new CodeBlock(),
          new HardBreak(),
          new Heading({ levels: [1, 2, 3] }),
          new HorizontalRule(),
          new ListItem(),
          new OrderedList(),
          new TodoItem(),
          new TodoList(),
          new Link(),
          new Bold(),
          new Code(),
          new Italic(),
          new Strike(),
          new Underline(),
          new History(),
        ],
        onUpdate: ({ getHTML }) => {
          this.inputListener(getHTML());
        },
      }),
    };
  },
  watch: {
    value: {
      handler: function (value) {
        let html = this.editor.getHTML();
        if (html !== this.value) {
          this.editor.setContent(value);
        }
      },
    },
  },
  beforeDestroy() {
    this.editor.destroy();
  },
  methods: {
    addLink(command, currentLink = null) {
      let href = currentLink.href || null;
      Dialog.prompt({
        message: this.$t("enter_url"),
        inputAttrs: {
          placeholder: "e.g. https://google.com",
          type: "url",
          value: href,
        },
        trapFocus: true,
        confirmText: href ? this.$t("update_link") : this.$t("add_link"),
        cancelText: this.$t("cancel"),
        onConfirm: (value) => command({ href: value }),
      });
    },
  },
};
</script>

<style lang="scss">
.editor {
  border: 1px solid #e1e1e1;
  .menubar {
    border-bottom: 1px solid #e1e1e1;
    .button {
      border-radius: 0;
    }
    .icon i {
      font-size: 14px;
    }
  }
  .content {
    margin-bottom: 5px;
  }
  .ProseMirror {
    outline: none;
    padding: 16px;
  }
}

.rich-text-field {
  .label {
    margin-bottom: 0.75rem;
  }
}
</style>
